import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../Accounts/AuthContext';
import { fetchClasses, fetchRoster, invalidateClasses, onClassesChanged } from './data/api';

const STORAGE_KEY = 'tdv3.selectedClassId';
// The pick is PER TAB (2026-10-26, fleet finding A-F1 / B-F1): sessionStorage holds
// this tab's class and localStorage only the default for a NEW tab. Before, every
// page read localStorage on mount, so a class picked in another tab silently
// became this tab's class on the next navigation (setup never names the class).
export function readSelectedClassId() {
  try { const own = window.sessionStorage.getItem(STORAGE_KEY); if (own) return own; } catch { /* no session storage */ }
  try { return window.localStorage.getItem(STORAGE_KEY); } catch { return null; }
}
export function writeSelectedClassId(id) {
  try { if (id) window.sessionStorage.setItem(STORAGE_KEY, id); else window.sessionStorage.removeItem(STORAGE_KEY); } catch { /* no session storage */ }
  try { if (id) window.localStorage.setItem(STORAGE_KEY, id); else window.localStorage.removeItem(STORAGE_KEY); } catch { /* non-persistent */ }
}
// One selection for the whole page (2026-10-24, F-2): every mounted
// useSelectedClass follows a pick made through any of them. Buffy's New class
// chip has its own instance, so its create used to select the new class for
// itself only, while the switcher stayed on the old one.
const followers = new Set();

// The selected class follows the teacher across Roster / Intervene / Gradebook.
// Real classes from claude-teacher-api (module-cached, one fetch per session);
// `cls` is null while loading or when signed out / no classes — pages guard.
// `auth` = { user, handleAPIResponse } and is what every api.js call takes, so
// token refresh piping (handleAPIResponse) rides along everywhere.
export default function useSelectedClass() {
  const { user, handleAPIResponse } = useAuth();
  const auth = useMemo(() => ({ user, handleAPIResponse }), [user, handleAPIResponse]);
  const [classes, setClasses] = useState(null); // null = loading, [] = none
  const [error, setError] = useState(null);
  const [classId, setClassId] = useState(() => readSelectedClassId());

  useEffect(() => {
    let alive = true;
    if (!user) { setClasses([]); setError('signed-out'); return undefined; }
    fetchClasses(auth)
      .then((list) => {
        if (alive) { setClasses(list); setError(null); }
        // Warm the selected class's roster immediately so Home and Insights
        // paint with real numbers instead of popping in from zeros.
        const storedId = readSelectedClassId();
        const sel = list.find((c) => c.id === storedId) || list[0];
        if (sel) fetchRoster(auth, sel.id).catch(() => {});
      })
      .catch((e) => { if (alive) { setClasses([]); setError(e.message || 'failed'); } });
    return () => { alive = false; };
  }, [user, auth]);

  // Re-read the class list when something outside this page mutates it (the
  // shell's New class modal notifies after invalidating the module cache).
  useEffect(() => {
    if (!user) return undefined;
    return onClassesChanged(() => {
      fetchClasses(auth).then((list) => setClasses(list)).catch(() => { /* keep last */ });
    });
  }, [user, auth]);

  useEffect(() => {
    followers.add(setClassId);
    return () => { followers.delete(setClassId); };
  }, []);

  const selectClass = useCallback((id) => {
    writeSelectedClassId(id);
    followers.forEach((follow) => follow(id));
    setClassId(id);
  }, []);

  // Force-refresh after mutations (start unit, create class, edit dates).
  const refreshClasses = useCallback(async () => {
    if (!user) return;
    invalidateClasses();
    try { setClasses(await fetchClasses(auth, { force: true })); } catch { /* keep last */ }
  }, [user, auth]);

  const list = classes || [];
  const cls = list.find((c) => c.id === classId) || list[0] || null;
  return { cls, classes: list, loading: classes === null, error, selectClass, refreshClasses, auth };
}
